import { useRestApiProvider } from "@jugl-web/rest-api";
import { PlainButton } from "@jugl-web/ui-kit";
import {
  cx,
  isEmailApiError,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { collapsedIconButtonClassName } from "@web-src/modules/client-support/pages/ClientSupportPage/consts";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useState } from "react";
import {
  EmailSetupScreenStep,
  ManageAccountsDrawerScreenToParametersMap,
} from "../../types";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { EnterEmailStep } from "./components/EnterEmailStep";
import {
  SelectSetupMethodStep,
  SetupMethod,
} from "./components/SelectSetupMethodStep";
import { VerifyEmailForwardingStep } from "./components/VerifyEmailForwardingStep";

const DISPLAY_TOTAL_STEPS = 2;

export const EmailSetupScreen: React.FC<{
  step?: EmailSetupScreenStep;
}> = ({ step: initialSetupStep = "selectMethod" }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { connectGmailAccount, emailAccount } = useEmailAccount();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [createEmailConfig, { isLoading: isCreatingConfig }] =
    integrationsApi.useCreateEmailConfigMutation();
  const [updateEmailConfig, { isLoading: isUpdatingConfig }] =
    integrationsApi.useUpdateEmailConfigMutation();

  const [step, setStep] = useState(() =>
    initialSetupStep === "enterEmail" ? 2 : 1
  );
  const [setupMethod, setSetupMethod] = useState<SetupMethod>("forwarding");
  const [email, setEmail] = useState("");
  const [forwardingEmail, setForwardingEmail] = useState("");

  const handleBack = useCallback(() => {
    if (step === 1) {
      transitionTo({ name: "connectedAccountList" });
      return;
    }

    transitionTo({ name: "emailAccount" });
  }, [step, transitionTo]);

  const handleCreateConfig = useCallback(async () => {
    try {
      const result = await createEmailConfig({
        entityId,
        meta: { silent: true },
      }).unwrap();

      setForwardingEmail(result.email);
      setStep(2);
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [createEmailConfig, entityId, t, toast]);

  const handleUpdateClientEmail = useCallback(async () => {
    try {
      await updateEmailConfig({
        entityId,
        data: {
          metadata: {
            ...emailAccount?.metadata,
            client_email: email,
          },
        },
        meta: { silent: true },
      }).unwrap();

      setStep(3);
    } catch (error) {
      if (
        isEmailApiError(error) &&
        error.data.errors.length > 0 &&
        error.data.errors[0].message === "email_already_exists"
      ) {
        toast(
          t({
            id: "client-support-page.email-already-exists",
            defaultMessage:
              "Selected email is already associated to another Jugl Workspace",
          }),
          { variant: "error" }
        );
        return;
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [email, emailAccount, entityId, t, toast, updateEmailConfig]);

  const handleContinue = useCallback(() => {
    if (step === 1) {
      if (setupMethod === "gmail") {
        connectGmailAccount();
        return;
      }

      handleCreateConfig();
      return;
    }

    if (step === 2) {
      handleUpdateClientEmail();
    }
  }, [
    connectGmailAccount,
    handleCreateConfig,
    handleUpdateClientEmail,
    setupMethod,
    step,
  ]);

  return (
    <div className="flex h-full justify-center overflow-y-auto bg-grey-100 p-8 max-lg:p-4">
      <div className="flex w-full max-w-300 flex-col gap-6 max-lg:gap-4">
        <div className="flex items-center gap-6 pl-2 max-lg:gap-3">
          <PlainButton
            className={cx(collapsedIconButtonClassName, "h-8 w-8")}
            onClick={handleBack}
          >
            <ArrowLeftIcon />
          </PlainButton>
          <div className="flex items-center gap-2">
            <EmailIcon className="h-7 w-7 shrink-0" />
            <div className="flex items-center gap-3">
              <span className="font-secondary text-xl font-medium text-black">
                {t({
                  id: "client-support-page.email",
                  defaultMessage: "Email",
                })}
              </span>
              {step > 1 && (
                <span className="font-secondary text-xs text-dark-600">
                  {t(
                    {
                      id: "client-support-page.email-setup-step",
                      defaultMessage: "Step {step} of {total}",
                    },
                    { step: step - 1, total: DISPLAY_TOTAL_STEPS }
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-solid border-grey-300 bg-white p-8 max-lg:p-5">
          {step === 1 && (
            <SelectSetupMethodStep
              setupMethod={setupMethod}
              onSetupMethodChange={setSetupMethod}
              onContinue={handleContinue}
              isLoading={isCreatingConfig}
            />
          )}

          {step === 2 && (
            <EnterEmailStep
              email={email}
              onEmailChange={setEmail}
              onContinue={handleContinue}
              isLoading={isUpdatingConfig}
            />
          )}

          {step === 3 && (
            <VerifyEmailForwardingStep
              forwardingEmail={forwardingEmail || emailAccount?.email || ""}
            />
          )}
        </div>
      </div>
    </div>
  );
};
